import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Download, X, Smartphone, Share } from 'lucide-react';

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Android: Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS: Show install button if not installed
    if (iOS && !window.navigator.standalone) {
      setShowInstallButton(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // iOS: Show instructions modal
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      // Android: Trigger native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        setShowInstallButton(false);
        
        // Show clear message for Android - check Downloads
        setTimeout(() => {
          alert('📥 Remindora İndiriliyor!\n\n1️⃣ Tarayıcınızın İNDİRİLENLER bölümünü açın\n2️⃣ "Remindora" dosyasını bulun\n3️⃣ Yükleme tamamlanınca ana ekranınızda görünecek\n\n💡 İndirme tamamlanana kadar bekleyin!');
        }, 500);
      } else {
        console.log('User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
    } else {
      // Fallback for browsers that don't support install prompt
      alert('📱 Tarayıcınız otomatik kurulumu desteklemiyor.\n\nAndroid: Chrome menü > "Ana ekrana ekle"\niOS: Safari paylaş butonu > "Ana Ekrana Ekle"');
    }
  };

  if (isInstalled) {
    return null;
  }

  // Always show button for now (testing)
  return (
    <>
      {/* Install Button */}
      <Button
        onClick={handleInstallClick}
        className="gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-lg w-full md:w-auto"
        size="sm"
      >
        <Download className="w-4 h-4" />
        Ana Ekrana Ekle
      </Button>

      {/* iOS Instructions Modal */}
      <Dialog open={showIOSInstructions} onOpenChange={setShowIOSInstructions}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-purple-600" />
              Ana Ekrana Ekle (iOS)
            </DialogTitle>
            <DialogDescription>
              Remindora'yı iPhone/iPad ana ekranınıza eklemek için aşağıdaki adımları izleyin:
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                1
              </div>
              <div>
                <p className="font-medium text-sm mb-1">Safari paylaş butonuna tıklayın</p>
                <div className="flex items-center gap-2 text-xs text-neutral-600">
                  <Share className="w-4 h-4" />
                  <span>Ekranın altındaki veya üstündeki paylaş ikonu</span>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                2
              </div>
              <div>
                <p className="font-medium text-sm mb-1">Aşağı kaydırın</p>
                <p className="text-xs text-neutral-600">
                  "Ana Ekrana Ekle" seçeneğini bulun
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center font-bold flex-shrink-0">
                3
              </div>
              <div>
                <p className="font-medium text-sm mb-1">"Ekle" butonuna basın</p>
                <p className="text-xs text-neutral-600">
                  Remindora artık ana ekranınızda!
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Not:</strong> Bu özellik sadece Safari tarayıcısında çalışır. Chrome veya başka bir tarayıcı kullanıyorsanız, lütfen Safari'ye geçin.
              </p>
            </div>
          </div>

          <Button
            onClick={() => setShowIOSInstructions(false)}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white"
          >
            Anladım
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
